import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { Reveal } from '@/components/ui/Reveal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useGameStore } from '@/state/gameStore';
import { hasActiveGame, MODE_LABELS } from '@/lib/saveGame';
import type { GameMode } from '@/state/types';

interface ModeRow {
  label: string;
  values: Record<GameMode, { text: string; tone?: 'positive' | 'negative' | 'neutral' }>;
}

const MODE_ORDER: GameMode[] = ['normal', 'realistic', 'sandbox'];

const MODE_LABEL_DISPLAY: Record<GameMode, string> = {
  normal: 'Klassik',
  realistic: 'Realistisch',
  sandbox: 'Sandbox',
};

const MODE_TAGLINE: Record<GameMode, string> = {
  normal: 'Der Einstieg',
  realistic: 'Für Profis',
  sandbox: 'Freies Spiel',
};

const MODE_CTA: Record<GameMode, string> = {
  normal: 'Klassik starten',
  realistic: 'Realistisch starten',
  sandbox: 'Sandbox starten',
};

const ROWS: ModeRow[] = [
  {
    label: 'Startkapital',
    values: {
      normal: { text: '3 Mio €' },
      realistic: { text: '2 Mio €' },
      sandbox: { text: 'unbegrenzt', tone: 'positive' },
    },
  },
  {
    label: 'Schwierigkeit',
    values: {
      normal: { text: 'Einsteiger' },
      realistic: { text: 'Hart', tone: 'negative' },
      sandbox: { text: 'spielerisch', tone: 'positive' },
    },
  },
  {
    label: 'Spielgeschwindigkeit',
    values: {
      normal: { text: '1× (Echtzeit)' },
      realistic: { text: '1× (Echtzeit)' },
      sandbox: { text: '1× / 2× / 5× / 10×', tone: 'positive' },
    },
  },
  {
    label: 'Pause',
    values: {
      normal: { text: '2 frei pro Jahr' },
      realistic: { text: '2 frei pro Jahr' },
      sandbox: { text: 'unbegrenzt', tone: 'positive' },
    },
  },
  {
    label: 'Tagesunterhalt',
    values: {
      normal: { text: 'aktiv', tone: 'positive' },
      realistic: { text: 'aktiv', tone: 'positive' },
      sandbox: { text: 'aktiv', tone: 'positive' },
    },
  },
  {
    label: 'Kredite',
    values: {
      normal: { text: 'verfügbar', tone: 'positive' },
      realistic: { text: 'verfügbar', tone: 'positive' },
      sandbox: { text: 'nicht verfügbar', tone: 'negative' },
    },
  },
  {
    label: 'Personalsystem',
    values: {
      normal: { text: 'aus' },
      realistic: { text: 'aus' },
      sandbox: { text: 'aus' },
    },
  },
  {
    label: 'Notruf-Frequenz',
    values: {
      normal: { text: 'regulär' },
      realistic: { text: 'regulär' },
      sandbox: { text: 'einstellbar', tone: 'positive' },
    },
  },
];

export function Modes() {
  const navigate = useNavigate();
  const resetGame = useGameStore((s) => s.resetGame);
  const currentMode = useGameStore((s) => s.mode);

  const [pendingMode, setPendingMode] = useState<GameMode | null>(null);

  const startMode = (mode: GameMode) => {
    if (hasActiveGame()) {
      setPendingMode(mode);
      return;
    }
    resetGame(mode);
    navigate(`/play?mode=${mode}`);
  };

  const confirmReset = () => {
    if (!pendingMode) return;
    const mode = pendingMode;
    resetGame(mode);
    setPendingMode(null);
    navigate(`/play?mode=${mode}`);
  };

  const continueExisting = () => setPendingMode(null);

  return (
    <section id="modes" className="relative px-4 py-24 md:px-6 md:py-32">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <div className="mb-12 max-w-3xl">
            <p className="mb-3 text-sm uppercase tracking-widest text-accent-emergency">
              Spielmodi
            </p>
            <h2 className="font-display text-4xl font-bold leading-tight md:text-5xl">
              Drei Modi, ein Spiel.
            </h2>
            <p className="mt-4 text-lg text-fg-secondary">
              Vergleiche die Modi auf einen Blick. Wechsle jederzeit im Einstellungs-Menü.
            </p>
          </div>
        </Reveal>

        {/* Comparison table */}
        <Reveal>
          <div className="overflow-hidden rounded-xl border border-border-subtle bg-bg-secondary">
            {/* Header */}
            <div className="grid grid-cols-1 border-b border-border-subtle md:grid-cols-[200px_repeat(3,1fr)]">
              <div className="hidden p-4 text-xs uppercase tracking-widest text-fg-muted md:block">
                Kriterium
              </div>
              {MODE_ORDER.map((m) => (
                <div
                  key={m}
                  className={[
                    'border-l border-border-subtle p-4',
                    m === 'realistic' ? 'bg-accent-emergency/5' : '',
                  ].join(' ')}
                >
                  <p className="text-xs uppercase tracking-widest text-fg-muted">
                    {MODE_TAGLINE[m]}
                  </p>
                  <p className="mt-1 font-display text-2xl font-bold">
                    {MODE_LABEL_DISPLAY[m]}
                  </p>
                </div>
              ))}
            </div>

            {/* Rows */}
            {ROWS.map((row, i) => (
              <div
                key={row.label}
                className={[
                  'grid grid-cols-2 md:grid-cols-[200px_repeat(3,1fr)]',
                  i > 0 ? 'border-t border-border-subtle' : '',
                ].join(' ')}
              >
                <div className="col-span-2 bg-bg-primary/40 p-3 text-sm font-semibold text-fg-secondary md:col-span-1 md:bg-transparent md:p-4">
                  {row.label}
                </div>
                {MODE_ORDER.map((m) => {
                  const v = row.values[m];
                  const tone =
                    v.tone === 'positive'
                      ? 'text-emerald-400'
                      : v.tone === 'negative'
                        ? 'text-rose-400'
                        : 'text-fg-primary';
                  const Icon =
                    v.tone === 'positive' ? Check : v.tone === 'negative' ? X : null;
                  return (
                    <div
                      key={m}
                      className={[
                        'flex items-center gap-2 border-l border-border-subtle p-3 text-sm md:p-4',
                        m === 'realistic' ? 'bg-accent-emergency/5' : '',
                      ].join(' ')}
                    >
                      {Icon && <Icon className={['h-4 w-4', tone].join(' ')} aria-hidden />}
                      <span className={tone}>{v.text}</span>
                    </div>
                  );
                })}
              </div>
            ))}

            {/* CTA row */}
            <div className="grid grid-cols-1 border-t border-border-subtle md:grid-cols-[200px_repeat(3,1fr)]">
              <div className="hidden p-4 md:block" />
              {MODE_ORDER.map((m) => (
                <div
                  key={m}
                  className={[
                    'border-l border-border-subtle p-4',
                    m === 'realistic' ? 'bg-accent-emergency/5' : '',
                  ].join(' ')}
                >
                  <button
                    type="button"
                    onClick={() => startMode(m)}
                    data-mode={m}
                    className={[
                      'w-full rounded-md px-4 py-2.5 text-sm font-semibold transition',
                      m === 'realistic'
                        ? 'bg-accent-emergency text-white hover:bg-accent-emergency-hover'
                        : 'border border-border-strong bg-bg-elevated text-fg-primary hover:bg-bg-elevated/70',
                    ].join(' ')}
                  >
                    {MODE_CTA[m]}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      <ConfirmDialog
        open={pendingMode !== null}
        title="Laufendes Spiel überschreiben?"
        description={
          pendingMode
            ? `Du hast bereits ein laufendes Spiel im Modus „${MODE_LABELS[currentMode]}". Ein neues Spiel im Modus „${MODE_LABELS[pendingMode]}" startet — der alte Spielstand geht dabei verloren.`
            : ''
        }
        confirmLabel="Neues Spiel starten"
        cancelLabel="Abbrechen"
        tone="danger"
        onConfirm={confirmReset}
        onCancel={continueExisting}
      />
    </section>
  );
}
